import { useState, useEffect } from 'react'
import {
  Mail,
  Clock,
  CheckCircle,
  XCircle,
  Trash2,
  RefreshCw,
  FileText,
  Building2,
  MapPin,
  Sparkles,
  Eye,
  Edit3,
  RotateCcw,
  Pencil,
  CheckSquare,
  Square,
  Loader2,
  ZoomIn,
  ZoomOut,
  Maximize2,
  Printer,
  Download,
  Check,
  Globe,
  Gift,
  Bell,
  Filter,
} from 'lucide-react'
import api, { API_BASE_URL } from '@/services/api'
import toast from 'react-hot-toast'
import LetterEditor from '@/components/LetterEditor'
import { useAuthStore } from '@/store/authStore'

interface Letter {
  id: number
  applicationId: number
  clientId: number
  content: string
  originalContent: string
  status: string
  qualityScore: number | null
  priority: number
  isFlagged: boolean
  flagNotes: string | null
  createdAt: string
  updatedAt: string
  approvedAt: string | null
  deletedAt: string | null
  deletionReason: string | null
  deletionNotes: string | null
  applicationReference: string | null
  applicationPdfPath: string | null
  clientName: string | null
  territoryName: string | null
  versionCount: number
}

interface LetterStats {
  pending: number
  inProgress: number
  approved: number
  rejected: number
  printed: number
  mailed: number
  deleted: number
  additional: number
  additional_requested: number
  total: number
}

interface Client {
  id: number
  companyName: string
  status?: string  // 'prospecting' | 'live' | 'retired'
}

interface Application {
  id: number
  referenceNumber: string
  territoryName: string
  status: string
}

type TabType = 'pending' | 'approved' | 'rejected' | 'requested' | 'deleted' | 'all'

// Store client data by client ID
const clientDataCache: Record<number, {
  logo: string | null
  portfolioImages: (string | null)[]
  websiteUrl: string | null
}> = {}

export default function Letters() {
  const [activeTab, setActiveTab] = useState<TabType>('pending')
  const [letters, setLetters] = useState<Letter[]>([])
  const [stats, setStats] = useState<LetterStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [selectedLetter, setSelectedLetter] = useState<Letter | null>(null)
  const [editingLetter, setEditingLetter] = useState<Letter | null>(null)
  const [clients, setClients] = useState<Client[]>([])
  const [pendingCountByClient, setPendingCountByClient] = useState<Record<number, number>>({})
  const [applications, setApplications] = useState<Application[]>([])
  const [generating, setGenerating] = useState(false)
  const [regenerating, setRegenerating] = useState(false)
  const [showGenerateModal, setShowGenerateModal] = useState(false)
  const [selectedAppIds, setSelectedAppIds] = useState<number[]>([])
  const [selectedClientId, setSelectedClientId] = useState<number | null>(null)
  const [selectedClientLogo, setSelectedClientLogo] = useState<string | null>(null)
  const [selectedClientPortfolio, setSelectedClientPortfolio] = useState<(string | null)[]>([null, null, null])
  const [selectedClientWebsite, setSelectedClientWebsite] = useState<string | null>(null)
  const [generationProgress, setGenerationProgress] = useState<{
    current: number
    total: number
    phase: string
    message: string
    successCount: number
    failedCount: number
    skippedCount: number
  } | null>(null)
  const [previewZoom, setPreviewZoom] = useState<number>(150) // 150% default
  const [selectedForPrint, setSelectedForPrint] = useState<number[]>([])
  const [printingBatch, setPrintingBatch] = useState(false)
  const [printingSingle, setPrintingSingle] = useState(false)

  // "All Letters" tab filters — only applied when activeTab === 'all'
  const [filterClientId, setFilterClientId] = useState<number | null>(null)
  const [filterStatus, setFilterStatus] = useState<string>('')
  const [filterCreatedAfter, setFilterCreatedAfter] = useState<string>('')
  const [filterCreatedBefore, setFilterCreatedBefore] = useState<string>('')

  useEffect(() => {
    fetchStats()  // also refreshes pendingCountByClient internally
    fetchClients()
  }, [])

  useEffect(() => {
    fetchLetters()
    // Refetch when any filter changes (only meaningful on the "all" tab,
    // but cheap to re-fire on tab switches too)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, filterClientId, filterStatus, filterCreatedAfter, filterCreatedBefore])

  // Fetch client data when letter is selected
  useEffect(() => {
    if (selectedLetter) {
      const clientId = selectedLetter.clientId
      if (clientDataCache[clientId] !== undefined) {
        setSelectedClientLogo(clientDataCache[clientId].logo)
        setSelectedClientPortfolio(clientDataCache[clientId].portfolioImages)
        setSelectedClientWebsite(clientDataCache[clientId].websiteUrl)
      } else {
        api.get(`/api/v1/clients/${clientId}`)
          .then(res => {
            const data = {
              logo: res.data.logoPath || null,
              portfolioImages: [
                res.data.portfolioImage1 || null,
                res.data.portfolioImage2 || null,
                res.data.portfolioImage3 || null,
              ],
              websiteUrl: res.data.websiteUrl || null,
            }
            clientDataCache[clientId] = data
            setSelectedClientLogo(data.logo)
            setSelectedClientPortfolio(data.portfolioImages)
            setSelectedClientWebsite(data.websiteUrl)
          })
          .catch(() => {
            const data = { logo: null, portfolioImages: [null, null, null], websiteUrl: null }
            clientDataCache[clientId] = data
            setSelectedClientLogo(null)
            setSelectedClientPortfolio([null, null, null])
            setSelectedClientWebsite(null)
          })
      }
    }
  }, [selectedLetter])

  const fetchStats = async () => {
    // Also refresh per-client pending counts — they always need to move together
    // (approve / reject / delete / generate all change the pending totals).
    fetchPendingCounts()
    try {
      const response = await api.get('/api/v1/letters/stats')
      setStats(response.data)
    } catch {
      // Stats fetch failed
    }
  }

  const fetchLetters = async () => {
    setLoading(true)
    try {
      let endpoint = '/api/v1/letters/'
      // For the pending / approved tabs, only the client filter applies.
      const clientQs = filterClientId != null ? `?client_id=${filterClientId}` : ''
      if (activeTab === 'pending') endpoint = `/api/v1/letters/pending${clientQs}`
      else if (activeTab === 'approved') endpoint = `/api/v1/letters/approved${clientQs}`
      else if (activeTab === 'deleted') endpoint = '/api/v1/letters/deleted'
      else if (activeTab === 'rejected') endpoint = '/api/v1/letters/?status=rejected'
      else if (activeTab === 'requested') endpoint = '/api/v1/letters/additional-requested'
      else if (activeTab === 'all') {
        // Apply the filter bar's selections as query params
        const params = new URLSearchParams()
        if (filterClientId != null) params.set('client_id', String(filterClientId))
        if (filterStatus) params.set('status', filterStatus)
        if (filterCreatedAfter) params.set('created_after', filterCreatedAfter)
        if (filterCreatedBefore) params.set('created_before', filterCreatedBefore)
        const qs = params.toString()
        endpoint = qs ? `/api/v1/letters/?${qs}` : '/api/v1/letters/'
      }

      const response = await api.get(endpoint)
      setLetters(response.data)
    } catch {
      toast.error('Failed to load letters')
    } finally {
      setLoading(false)
    }
  }

  // Only client applies on pending/approved; everything applies on all.
  const filterBarVisible = activeTab === 'all' || activeTab === 'pending' || activeTab === 'approved'
  const hasActiveFilters =
    (activeTab === 'all' &&
      (filterClientId != null || filterStatus !== '' || filterCreatedAfter !== '' || filterCreatedBefore !== '')) ||
    ((activeTab === 'pending' || activeTab === 'approved') && filterClientId != null)

  const clearFilters = () => {
    setFilterClientId(null)
    setFilterStatus('')
    setFilterCreatedAfter('')
    setFilterCreatedBefore('')
  }

  const fetchClients = async () => {
    try {
      const response = await api.get('/api/v1/clients/')
      setClients(response.data)
    } catch {
      // Clients fetch failed
    }
  }

  const fetchPendingCounts = async () => {
    try {
      const response = await api.get('/api/v1/letters/pending-counts')
      setPendingCountByClient(response.data || {})
    } catch {
      // Pending counts fetch failed — non-fatal, dropdowns just won't show counts
    }
  }

  // Clients eligible to appear in dropdowns — excludes retired clients so we
  // never target them for new work or filter for their pending queue.
  const eligibleClients = clients.filter((c) => c.status !== 'retired')

  // Label helper: "Client Name" or "Client Name (N pending)"
  const clientLabel = (c: Client) => {
    const n = pendingCountByClient[c.id] || 0
    return n > 0 ? `${c.companyName} (${n} pending)` : c.companyName
  }

  const fetchApplications = async (clientId: number) => {
    try {
      // Get applications that don't already have letters for this client
      const response = await api.get(`/api/v1/applications/available-for-client/${clientId}`)
      setApplications(response.data)
    } catch {
      setApplications([])
    }
  }

  const selectNextLetter = (currentId: number) => {
    const currentIndex = letters.findIndex(l => l.id === currentId)
    if (currentIndex >= 0 && letters.length > 1) {
      // Select next letter, or previous if at end
      const nextIndex = currentIndex < letters.length - 1 ? currentIndex + 1 : currentIndex - 1
      setSelectedLetter(letters[nextIndex])
    } else {
      setSelectedLetter(null)
    }
  }

  const handleApprove = async (letterId: number) => {
    try {
      await api.post(`/api/v1/letters/${letterId}/approve`)
      toast.success('Letter approved')
      selectNextLetter(letterId)
      fetchLetters()
      fetchStats()
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to approve letter')
    }
  }

  const handleReject = async (letterId: number) => {
    try {
      await api.post(`/api/v1/letters/${letterId}/reject`, { reason: 'Rejected' })
      toast.success('Letter rejected')
      selectNextLetter(letterId)
      fetchLetters()
      fetchStats()
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to reject letter')
    }
  }

  const handleDelete = async (letterId: number) => {
    try {
      await api.delete(`/api/v1/letters/${letterId}`, { 
        data: { reason: 'Deleted', deletion_reason: 'other' } 
      })
      toast.success('Letter deleted')
      fetchLetters()
      fetchStats()
      setSelectedLetter(null)
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to delete letter')
    }
  }

  const handleRestore = async (letterId: number) => {
    try {
      await api.post(`/api/v1/letters/${letterId}/restore`)
      toast.success('Letter restored')
      fetchLetters()
      fetchStats()
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to restore letter')
    }
  }

  const handleMarkAdditional = async (letterId: number) => {
    try {
      await api.post(`/api/v1/letters/${letterId}/mark-additional`)
      toast.success('Letter marked as additional')
      selectNextLetter(letterId)
      fetchLetters()
      fetchStats()
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to mark letter as additional')
    }
  }

  const handleRegenerate = async (letterId: number) => {
    setRegenerating(true)
    try {
      const response = await api.post(`/api/v1/letters/${letterId}/regenerate`, {})
      toast.success('Letter regenerated')
      fetchLetters()
      setSelectedLetter(response.data)
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to regenerate letter')
    } finally {
      setRegenerating(false)
    }
  }

  // Print functions
  const handlePrintSingle = async (letterId: number) => {
    setPrintingSingle(true)
    try {
      // Generate/save PDF on server and get it for printing
      const response = await api.get(`/api/v1/letters/${letterId}/pdf?download=false&save=true`, {
        responseType: 'blob',
      })
      
      // Open PDF in new window and trigger print
      const blob = new Blob([response.data], { type: 'application/pdf' })
      const url = window.URL.createObjectURL(blob)
      const printWindow = window.open(url, '_blank')
      
      if (printWindow) {
        printWindow.onload = () => {
          printWindow.print()
        }
      }
      
      toast.success('PDF ready for printing')
    } catch (error: any) {
      toast.error('Failed to generate PDF')
    } finally {
      setPrintingSingle(false)
    }
  }

  const handlePrintBatch = async (markAsPrinted: boolean = false) => {
    if (selectedForPrint.length === 0) {
      toast.error('No letters selected for printing')
      return
    }
    
    setPrintingBatch(true)
    try {
      const response = await api.post('/api/v1/letters/batch-pdf', {
        letter_ids: selectedForPrint,
        mark_as_printed: markAsPrinted,
        save_individual: true,
      }, {
        responseType: 'blob',
      })
      
      // Open PDF in new window and trigger print
      const blob = new Blob([response.data], { type: 'application/pdf' })
      const url = window.URL.createObjectURL(blob)
      const printWindow = window.open(url, '_blank')
      
      if (printWindow) {
        printWindow.onload = () => {
          printWindow.print()
        }
      }
      
      toast.success(`${selectedForPrint.length} letters ready for printing`)
      
      if (markAsPrinted) {
        setSelectedForPrint([])
        fetchLetters()
        fetchStats()
      }
    } catch (error: any) {
      toast.error('Failed to generate batch PDF')
    } finally {
      setPrintingBatch(false)
    }
  }

  const handlePrintAllApproved = async (markAsPrinted: boolean = false) => {
    const approvedLetters = letters.filter(l => l.status === 'approved')
    if (approvedLetters.length === 0) {
      toast.error('No approved letters to print')
      return
    }
    
    setSelectedForPrint(approvedLetters.map(l => l.id))
    
    setPrintingBatch(true)
    try {
      const response = await api.post('/api/v1/letters/batch-pdf', {
        letter_ids: approvedLetters.map(l => l.id),
        mark_as_printed: markAsPrinted,
        save_individual: true,
      }, {
        responseType: 'blob',
      })
      
      // Open PDF in new window and trigger print
      const blob = new Blob([response.data], { type: 'application/pdf' })
      const url = window.URL.createObjectURL(blob)
      const printWindow = window.open(url, '_blank')
      
      if (printWindow) {
        printWindow.onload = () => {
          printWindow.print()
        }
      }
      
      toast.success(`${approvedLetters.length} letters ready for printing`)
      
      if (markAsPrinted) {
        setSelectedForPrint([])
        fetchLetters()
        fetchStats()
      }
    } catch (error: any) {
      toast.error('Failed to generate batch PDF')
    } finally {
      setPrintingBatch(false)
    }
  }

  const toggleSelectForPrint = (letterId: number) => {
    setSelectedForPrint(prev => 
      prev.includes(letterId) 
        ? prev.filter(id => id !== letterId)
        : [...prev, letterId]
    )
  }

  const toggleSelectAllForPrint = () => {
    const approvedLetters = letters.filter(l => l.status === 'approved')
    if (selectedForPrint.length === approvedLetters.length) {
      setSelectedForPrint([])
    } else {
      setSelectedForPrint(approvedLetters.map(l => l.id))
    }
  }

  const handleGenerate = async () => {
    if (selectedAppIds.length === 0 || !selectedClientId) {
      toast.error('Please select at least one application and a client')
      return
    }
    
    setGenerating(true)
    setGenerationProgress({
      current: 0,
      total: selectedAppIds.length,
      phase: 'starting',
      message: 'Starting batch generation...',
      successCount: 0,
      failedCount: 0,
      skippedCount: 0,
    })
    
    try {
      // Get auth token from store
      const token = useAuthStore.getState().accessToken
      
      // Use fetch with SSE for streaming progress
      const response = await fetch(`${API_BASE_URL}/api/v1/letters/generate-batch`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          application_ids: selectedAppIds,
          client_id: selectedClientId,
        }),
      })
      
      if (!response.ok) {
        throw new Error('Failed to start generation')
      }
      
      const reader = response.body?.getReader()
      const decoder = new TextDecoder()
      
      if (!reader) {
        throw new Error('No response stream')
      }
      
      let successCount = 0
      let failedCount = 0
      let skippedCount = 0
      
      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        
        const text = decoder.decode(value)
        const lines = text.split('\n')
        
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6))
              
              if (data.type === 'progress') {
                setGenerationProgress({
                  current: data.current,
                  total: data.total,
                  phase: data.phase,
                  message: data.message,
                  successCount,
                  failedCount,
                  skippedCount,
                })
              } else if (data.type === 'item_complete') {
                successCount++
                setGenerationProgress(prev => prev ? {
                  ...prev,
                  current: data.current,
                  message: data.message,
                  successCount,
                } : null)
              } else if (data.type === 'item_error') {
                failedCount++
                setGenerationProgress(prev => prev ? {
                  ...prev,
                  current: data.current,
                  message: data.message,
                  failedCount,
                } : null)
              } else if (data.type === 'item_skipped') {
                skippedCount++
                setGenerationProgress(prev => prev ? {
                  ...prev,
                  current: data.current,
                  message: data.message,
                  skippedCount,
                } : null)
              } else if (data.type === 'complete') {
                // Final results
                if (data.success > 0) {
                  toast.success(`Generated ${data.success} letter(s)`)
                }
                if (data.skipped > 0) {
                  toast.success(`Skipped ${data.skipped} (already exist)`, { icon: '⏭️' })
                }
                if (data.failed > 0) {
                  toast.error(`Failed to generate ${data.failed} letter(s)`)
                }
              } else if (data.type === 'error') {
                toast.error(data.message)
              }
            } catch (e) {
              // Ignore parse errors for incomplete chunks
            }
          }
        }
      }
      
      setShowGenerateModal(false)
      setSelectedAppIds([])
      fetchLetters()
      fetchStats()
      // Refresh applications list to remove completed ones
      if (selectedClientId) {
        fetchApplications(selectedClientId)
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to generate letters')
    } finally {
      setGenerating(false)
      setGenerationProgress(null)
    }
  }
  
  const toggleAppSelection = (appId: number) => {
    setSelectedAppIds(prev => 
      prev.includes(appId) 
        ? prev.filter(id => id !== appId)
        : [...prev, appId]
    )
  }
  
  const selectAllApps = () => {
    if (selectedAppIds.length === applications.length) {
      setSelectedAppIds([])
    } else {
      setSelectedAppIds(applications.map(app => app.id))
    }
  }

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      pending: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
      in_progress: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      approved: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
      rejected: 'bg-red-500/20 text-red-400 border-red-500/30',
      printed: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
      mailed: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
      deleted: 'bg-slate-500/20 text-slate-400 border-slate-500/30',
      additional: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
      additional_requested: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    }

    const icons: Record<string, React.ReactNode> = {
      pending: <Clock className="w-3 h-3" />,
      in_progress: <Edit3 className="w-3 h-3" />,
      approved: <CheckCircle className="w-3 h-3" />,
      rejected: <XCircle className="w-3 h-3" />,
      deleted: <Trash2 className="w-3 h-3" />,
      additional: <Gift className="w-3 h-3" />,
      additional_requested: <Bell className="w-3 h-3" />,
    }
    
    return (
      <span className={`inline-flex items-center gap-1.5 px-2 py-1 text-xs font-medium rounded-full border ${styles[status] || styles.pending}`}>
        {icons[status]}
        {status.replace('_', ' ').charAt(0).toUpperCase() + status.slice(1).replace('_', ' ')}
      </span>
    )
  }

  const tabs = [
    { id: 'pending' as TabType, label: 'Pending Review', count: stats?.pending || 0, icon: Clock },
    { id: 'approved' as TabType, label: 'Approved', count: stats?.approved || 0, icon: CheckCircle },
    { id: 'rejected' as TabType, label: 'Rejected', count: stats?.rejected || 0, icon: XCircle },
    { id: 'requested' as TabType, label: 'Requested', count: stats?.additional_requested || 0, icon: Bell },
    { id: 'deleted' as TabType, label: 'Deleted', count: stats?.deleted || 0, icon: Trash2 },
    { id: 'all' as TabType, label: 'All Letters', count: stats?.total || 0, icon: Mail },
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-100">Letters</h1>
          <p className="text-slate-400 mt-1">Generate and manage prospecting letters</p>
        </div>
        <div className="flex items-center gap-2">
          {activeTab === 'approved' && letters.length > 0 && (
            <>
              <button
                onClick={() => handlePrintBatch(false)}
                disabled={printingBatch || selectedForPrint.length === 0}
                className="btn btn-secondary flex items-center gap-2 disabled:opacity-50"
              >
                {printingBatch ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                Export Selected ({selectedForPrint.length})
              </button>
              <button
                onClick={() => handlePrintAllApproved(false)}
                disabled={printingBatch}
                className="btn btn-secondary flex items-center gap-2 disabled:opacity-50"
              >
                {printingBatch ? <Loader2 className="w-4 h-4 animate-spin" /> : <Printer className="w-4 h-4" />}
                Export All ({letters.length})
              </button>
              <button
                onClick={() => handlePrintAllApproved(true)}
                disabled={printingBatch}
                className="btn bg-emerald-600 hover:bg-emerald-700 text-white flex items-center gap-2 disabled:opacity-50"
                title="Export all and mark as printed"
              >
                {printingBatch ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                Print & Complete
              </button>
            </>
          )}
          <button
            onClick={() => {
              setShowGenerateModal(true)
              if (clients.length > 0) {
                setSelectedClientId(clients[0].id)
                fetchApplications(clients[0].id)
              }
            }}
            className="btn-primary flex items-center gap-2"
          >
            <Sparkles className="w-4 h-4" />
            Generate Letter
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-amber-500/20">
              <Clock className="w-5 h-5 text-amber-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-100">{stats?.pending || 0}</p>
              <p className="text-sm text-slate-400">Pending</p>
            </div>
          </div>
        </div>
        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-emerald-500/20">
              <CheckCircle className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-100">{stats?.approved || 0}</p>
              <p className="text-sm text-slate-400">Approved</p>
            </div>
          </div>
        </div>
        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-red-500/20">
              <XCircle className="w-5 h-5 text-red-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-100">{stats?.rejected || 0}</p>
              <p className="text-sm text-slate-400">Rejected</p>
            </div>
          </div>
        </div>
        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-500/20">
              <Mail className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-100">{stats?.total || 0}</p>
              <p className="text-sm text-slate-400">Total</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-700/50 pb-2">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === tab.id
                ? 'bg-[#f39c13] text-white'
                : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/50'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
            <span className={`px-1.5 py-0.5 text-xs rounded-full ${
              activeTab === tab.id ? 'bg-white/20' : 'bg-slate-700'
            }`}>
              {tab.count}
            </span>
          </button>
        ))}
      </div>

      {/* Filter bar — client filter on pending/approved/all; status+date only on all */}
      {filterBarVisible && (
        <div className="card p-4">
          <div className="flex items-center gap-2 mb-3">
            <Filter className="w-4 h-4 text-slate-400" />
            <h3 className="text-sm font-medium text-slate-300">Filter letters</h3>
          </div>
          <div className="flex flex-wrap gap-3 items-end">
            <div className="flex-1 min-w-[180px]">
              <label className="block text-xs font-medium text-slate-400 mb-1.5">Client</label>
              <select
                className="input w-full"
                value={filterClientId ?? ''}
                onChange={(e) => setFilterClientId(e.target.value ? Number(e.target.value) : null)}
              >
                <option value="">All clients</option>
                {eligibleClients.map((c) => (
                  <option key={c.id} value={c.id}>{clientLabel(c)}</option>
                ))}
              </select>
            </div>
            {activeTab === 'all' && (
              <>
                <div className="flex-1 min-w-[160px]">
                  <label className="block text-xs font-medium text-slate-400 mb-1.5">Status</label>
                  <select
                    className="input w-full"
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                  >
                    <option value="">All statuses</option>
                    <option value="pending">Pending</option>
                    <option value="in_progress">In progress</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                    <option value="printed">Printed</option>
                    <option value="mailed">Mailed</option>
                    <option value="additional">Additional</option>
                    <option value="additional_requested">Additional requested</option>
                    <option value="deleted">Deleted</option>
                  </select>
                </div>
                <div className="flex-1 min-w-[150px]">
                  <label className="block text-xs font-medium text-slate-400 mb-1.5">Created from</label>
                  <input
                    type="date"
                    className="input w-full"
                    value={filterCreatedAfter}
                    onChange={(e) => setFilterCreatedAfter(e.target.value)}
                    max={filterCreatedBefore || undefined}
                  />
                </div>
                <div className="flex-1 min-w-[150px]">
                  <label className="block text-xs font-medium text-slate-400 mb-1.5">Created to</label>
                  <input
                    type="date"
                    className="input w-full"
                    value={filterCreatedBefore}
                    onChange={(e) => setFilterCreatedBefore(e.target.value)}
                    min={filterCreatedAfter || undefined}
                  />
                </div>
              </>
            )}
            {hasActiveFilters && (
              <button onClick={clearFilters} className="btn-secondary text-sm">
                Clear filters
              </button>
            )}
          </div>
          {hasActiveFilters && (
            <p className="mt-3 text-xs text-slate-500">
              Showing {letters.length} matching letter{letters.length === 1 ? '' : 's'}
            </p>
          )}
        </div>
      )}

      {/* Content */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 flex-1" style={{ minHeight: 'calc(100vh - 280px)' }}>
        {/* Letter List */}
        <div className="card overflow-hidden lg:col-span-2 flex flex-col" style={{ maxHeight: 'calc(100vh - 220px)' }}>
          <div className="px-4 py-3 bg-slate-800/30 border-b border-slate-700/50">
            <div className="flex items-center justify-between">
              <h3 className="font-medium text-slate-200">
                {tabs.find(t => t.id === activeTab)?.label}
              </h3>
              {activeTab === 'approved' && letters.length > 0 && (
                <div className="flex items-center gap-2">
                  <button
                    onClick={toggleSelectAllForPrint}
                    className="p-1.5 rounded hover:bg-slate-700/50 text-slate-400 hover:text-slate-200"
                    title={selectedForPrint.length === letters.length ? "Deselect all" : "Select all"}
                  >
                    {selectedForPrint.length === letters.length ? (
                      <CheckSquare className="w-4 h-4" />
                    ) : (
                      <Square className="w-4 h-4" />
                    )}
                  </button>
                  {selectedForPrint.length > 0 && (
                    <span className="text-xs text-slate-400">{selectedForPrint.length} selected</span>
                  )}
                </div>
              )}
            </div>
          </div>
          
          {loading ? (
            <div className="p-8 text-center">
              <RefreshCw className="w-8 h-8 text-slate-500 animate-spin mx-auto mb-2" />
              <p className="text-slate-400">Loading letters...</p>
            </div>
          ) : letters.length === 0 ? (
            <div className="p-8 text-center">
              <Mail className="w-12 h-12 text-slate-500 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-200 mb-2">No letters found</h3>
              <p className="text-slate-400">
                {activeTab === 'pending' 
                  ? 'Generate a letter to get started'
                  : 'No letters in this category'}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-slate-700/30 overflow-y-auto flex-1 scrollbar-thin">
              {letters.map((letter) => (
                <div
                  key={letter.id}
                  onClick={() => setSelectedLetter(letter)}
                  onDoubleClick={() => letter.status !== 'deleted' && setEditingLetter(letter)}
                  className={`p-4 cursor-pointer hover:bg-slate-800/30 transition-colors ${
                    selectedLetter?.id === letter.id ? 'bg-slate-800/50' : ''
                  }`}
                  title="Double-click to edit"
                >
                  <div className="flex items-start gap-3">
                    {/* Checkbox for approved letters */}
                    {activeTab === 'approved' && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          toggleSelectForPrint(letter.id)
                        }}
                        className="mt-0.5 p-0.5 rounded hover:bg-slate-700/50"
                      >
                        {selectedForPrint.includes(letter.id) ? (
                          <CheckSquare className="w-4 h-4 text-emerald-400" />
                        ) : (
                          <Square className="w-4 h-4 text-slate-500" />
                        )}
                      </button>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Building2 className="w-4 h-4 text-slate-500" />
                        <span className="font-medium text-slate-200 truncate">
                          {letter.clientName || 'Unknown Client'}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-slate-400">
                        <FileText className="w-3 h-3" />
                        <span className="truncate">{letter.applicationReference || 'No ref'}</span>
                      </div>
                      {letter.territoryName && (
                        <div className="flex items-center gap-2 text-sm text-slate-500 mt-1">
                          <MapPin className="w-3 h-3" />
                          <span>{letter.territoryName}</span>
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      {getStatusBadge(letter.status)}
                      <span className="text-xs text-slate-500">
                        {new Date(letter.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Letter Preview */}
        <div className="card overflow-hidden lg:col-span-3 flex flex-col">
          <div className="px-4 py-3 bg-slate-800/30 border-b border-slate-700/50 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <h3 className="font-medium text-slate-200">Letter Preview</h3>
              {selectedLetter && (
                <span
                  className="text-xs bg-slate-700/50 text-slate-300 px-2 py-1 rounded-md border border-slate-600/50"
                  title="Date the letter was generated"
                >
                  Generated {new Date(selectedLetter.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                </span>
              )}
              {selectedLetter && (
                <div className="flex items-center gap-1 bg-slate-700/50 rounded-lg p-0.5">
                  <button
                    onClick={() => setPreviewZoom(Math.max(50, previewZoom - 25))}
                    className="p-1 rounded hover:bg-slate-600 text-slate-400 hover:text-slate-200"
                    title="Zoom out"
                  >
                    <ZoomOut className="w-4 h-4" />
                  </button>
                  <span className="text-xs text-slate-400 w-10 text-center">{previewZoom}%</span>
                  <button
                    onClick={() => setPreviewZoom(Math.min(150, previewZoom + 25))}
                    className="p-1 rounded hover:bg-slate-600 text-slate-400 hover:text-slate-200"
                    title="Zoom in"
                  >
                    <ZoomIn className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setPreviewZoom(100)}
                    className="p-1 rounded hover:bg-slate-600 text-slate-400 hover:text-slate-200"
                    title="Reset zoom"
                  >
                    <Maximize2 className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
            {selectedLetter && (
              <div className="flex items-center gap-2">
                {selectedLetter.status === 'pending' && (
                  <>
                    <button
                      onClick={() => handleApprove(selectedLetter.id)}
                      className="p-1.5 rounded hover:bg-emerald-500/20 text-emerald-400"
                      title="Approve"
                    >
                      <CheckCircle className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleReject(selectedLetter.id)}
                      className="p-1.5 rounded hover:bg-red-500/20 text-red-400"
                      title="Reject"
                    >
                      <XCircle className="w-4 h-4" />
                    </button>
                  </>
                )}
                {(selectedLetter.status === 'pending' || selectedLetter.status === 'rejected') && (
                  <button
                    onClick={() => handleMarkAdditional(selectedLetter.id)}
                    className="p-1.5 rounded hover:bg-orange-500/20 text-orange-400"
                    title="Mark as Additional"
                  >
                    <Gift className="w-4 h-4" />
                  </button>
                )}
                {selectedLetter.status === 'additional_requested' && (
                  <button
                    onClick={() => handleApprove(selectedLetter.id)}
                    className="p-1.5 rounded hover:bg-emerald-500/20 text-emerald-400"
                    title="Approve requested letter"
                  >
                    <CheckCircle className="w-4 h-4" />
                  </button>
                )}
                {selectedLetter.status !== 'deleted' && (
                  <>
                    <button
                      onClick={() => handlePrintSingle(selectedLetter.id)}
                      disabled={printingSingle}
                      className="p-1.5 rounded hover:bg-purple-500/20 text-purple-400 disabled:opacity-50"
                      title="Download PDF"
                    >
                      {printingSingle ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                    </button>
                    <button
                      onClick={() => setEditingLetter(selectedLetter)}
                      className="p-1.5 rounded hover:bg-[#f39c13]/20 text-[#f39c13]"
                      title="Edit letter"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleRegenerate(selectedLetter.id)}
                      className="p-1.5 rounded hover:bg-blue-500/20 text-blue-400"
                      title="Regenerate"
                    >
                      <RefreshCw className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(selectedLetter.id)}
                      className="p-1.5 rounded hover:bg-slate-500/20 text-slate-400"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </>
                )}
                {selectedLetter.status === 'deleted' && (
                  <button
                    onClick={() => handleRestore(selectedLetter.id)}
                    className="p-1.5 rounded hover:bg-amber-500/20 text-amber-400"
                    title="Restore"
                  >
                    <RotateCcw className="w-4 h-4" />
                  </button>
                )}
              </div>
            )}
          </div>
          
          {selectedLetter ? (
            <div className="p-4 relative">
              {/* Regeneration Overlay */}
              {regenerating && (
                <div className="absolute inset-0 bg-slate-800/90 z-10 flex flex-col items-center justify-center rounded-lg">
                  <RefreshCw className="w-10 h-10 text-blue-500 animate-spin mb-3" />
                  <p className="text-slate-200 font-medium">Regenerating...</p>
                  <div className="mt-3 w-48 h-1.5 bg-slate-700 rounded-full overflow-hidden">
                    <div className="h-full bg-blue-500 rounded-full animate-pulse" style={{ width: '100%' }} />
                  </div>
                </div>
              )}
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <h4 className="font-medium text-slate-200">{selectedLetter.clientName}</h4>
                  <p className="text-sm text-slate-400">{selectedLetter.applicationReference}</p>
                </div>
                <div className="flex items-center gap-2">
                  {selectedLetter.applicationPdfPath && (
                    <button
                      onClick={async () => {
                        try {
                          // SEC-001: fetch via authed endpoint
                          const resp = await api.get(
                            `/api/v1/applications/${selectedLetter.applicationId}/source-pdf`,
                            { responseType: 'blob' },
                          )
                          const url = URL.createObjectURL(resp.data)
                          window.open(url, '_blank')
                        } catch (err: any) {
                          toast.error(err.response?.data?.detail || 'Failed to load source PDF')
                        }
                      }}
                      className="btn btn-secondary text-xs px-2 py-1 flex items-center gap-1"
                      title="Open Source PDF"
                    >
                      <FileText className="w-3 h-3" />
                      PDF
                    </button>
                  )}
                  {getStatusBadge(selectedLetter.status)}
                </div>
              </div>
              
              <div className="overflow-auto flex-1 p-4 bg-slate-600/20" style={{ maxHeight: 'calc(100vh - 400px)' }}>
                {/* Wrapper to allow scrolling of scaled content */}
                <div style={{ 
                  width: `${595 * previewZoom / 100}px`, 
                  height: `${842 * previewZoom / 100}px`,
                  margin: '0 auto',
                  position: 'relative'
                }}>
                  {/* A4 paper - fixed size 595x842px, scaled by zoom */}
                  <div 
                    className="bg-white rounded shadow-lg absolute top-0 left-0"
                    style={{ 
                      width: '595px', 
                      height: '842px',
                      transform: `scale(${previewZoom / 100})`,
                      transformOrigin: 'top left',
                      overflow: 'hidden'
                    }}
                  >
                    {/* Logo header */}
                    <div className="pt-10 px-12 pb-2" style={{ position: 'absolute', top: 0, left: 0, right: 0 }}>
                      {selectedClientLogo && (
                        <div className="flex justify-end">
                          <img 
                            src={selectedClientLogo.startsWith('/') ? selectedClientLogo : `/${selectedClientLogo}`}
                            alt={selectedLetter.clientName || 'Logo'}
                            className="max-h-16 max-w-[160px] object-contain"
                          />
                        </div>
                      )}
                    </div>
                    {/* Letter content - positioned absolutely so it doesn't stretch */}
                    <div 
                      className="px-12 text-slate-900 letter-preview absolute"
                      style={{ 
                        fontFamily: "'Aptos', 'Calibri', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif", 
                        fontSize: '10px',
                        lineHeight: '1.4',
                        top: selectedClientLogo ? '70px' : '35px',
                        left: 0,
                        right: 0
                      }}
                      dangerouslySetInnerHTML={{ 
                        __html: selectedLetter.content.includes('<p>') 
                          ? selectedLetter.content 
                          : selectedLetter.content.replace(/\n/g, '<br>') 
                      }}
                    />
                    
                    {/* Portfolio Images & Footer - positioned at bottom */}
                    {(selectedClientPortfolio.some(img => img) || selectedClientWebsite) && (
                      <div className="absolute bottom-6 left-12 right-12">
                        {/* Portfolio Images */}
                        {selectedClientPortfolio.some(img => img) && (
                          <div className="mb-2">
                            <div className="border-t border-gray-300 mb-2"></div>
                            <div className="flex gap-2 justify-center">
                              {selectedClientPortfolio.map((img, idx) => (
                                img ? (
                                  <img
                                    key={idx}
                                    src={img.startsWith('/') ? img : `/${img}`}
                                    alt={`Portfolio ${idx + 1}`}
                                    className="h-12 w-auto object-cover rounded"
                                    onError={(e) => {
                                      e.currentTarget.style.display = 'none'
                                    }}
                                  />
                                ) : null
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Website Footer */}
                        {selectedClientWebsite && (
                          <>
                            <div className="border-t border-gray-300 mb-1"></div>
                            <div className="text-center text-gray-500 flex items-center justify-center gap-1" style={{ fontSize: '7px' }}>
                              <Globe className="w-2 h-2" />
                              {selectedClientWebsite}
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              {selectedLetter.versionCount > 0 && (
                <p className="mt-3 text-xs text-slate-500">
                  {selectedLetter.versionCount} version(s) saved
                </p>
              )}
            </div>
          ) : (
            <div className="p-8 text-center">
              <Eye className="w-12 h-12 text-slate-500 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-200 mb-2">Select a letter</h3>
              <p className="text-slate-400">Click on a letter to preview its content</p>
            </div>
          )}
        </div>
      </div>

      {/* Generate Modal */}
      {showGenerateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="card w-full max-w-2xl mx-4 max-h-[80vh] flex flex-col">
            <div className="px-4 py-3 border-b border-slate-700/50 flex items-center justify-between">
              <h3 className="font-medium text-slate-200">Generate Letters</h3>
              <button
                onClick={() => setShowGenerateModal(false)}
                className="text-slate-400 hover:text-slate-200"
                disabled={generating}
              >
                <XCircle className="w-5 h-5" />
              </button>
            </div>
            
            {generating && generationProgress ? (
              <div className="p-8 flex flex-col items-center justify-center">
                <Loader2 className="w-12 h-12 text-blue-500 animate-spin mb-4" />
                <p className="text-slate-200 text-lg font-medium">Generating Letters</p>
                
                {/* Current action message */}
                <p className="text-blue-400 text-sm mt-3 font-medium">
                  {generationProgress.message}
                </p>
                
                {/* Progress bar */}
                <div className="mt-4 w-full max-w-sm">
                  <div className="flex justify-between text-xs text-slate-400 mb-1">
                    <span>{generationProgress.current} of {generationProgress.total}</span>
                    <span>{Math.round((generationProgress.current / generationProgress.total) * 100)}%</span>
                  </div>
                  <div className="h-3 bg-slate-700 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-blue-500 rounded-full transition-all duration-300"
                      style={{ width: `${(generationProgress.current / generationProgress.total) * 100}%` }}
                    />
                  </div>
                </div>
                
                {/* Stats */}
                <div className="mt-4 flex gap-4 text-xs">
                  {generationProgress.successCount > 0 && (
                    <span className="text-emerald-400">
                      ✓ {generationProgress.successCount} completed
                    </span>
                  )}
                  {generationProgress.skippedCount > 0 && (
                    <span className="text-amber-400">
                      ⏭ {generationProgress.skippedCount} skipped
                    </span>
                  )}
                  {generationProgress.failedCount > 0 && (
                    <span className="text-red-400">
                      ✗ {generationProgress.failedCount} failed
                    </span>
                  )}
                </div>
                
                <p className="text-slate-500 text-xs mt-6">Please don't close this window.</p>
              </div>
            ) : (
              <div className="p-4 space-y-4 overflow-auto">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Client
                  </label>
                  <select
                    value={selectedClientId || ''}
                    onChange={(e) => {
                      const id = Number(e.target.value)
                      setSelectedClientId(id)
                      setSelectedAppIds([])
                      fetchApplications(id)
                    }}
                    className="input w-full"
                  >
                    <option value="">Select a client...</option>
                    {eligibleClients.map((client) => (
                      <option key={client.id} value={client.id}>
                        {clientLabel(client)}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-slate-300">
                      Applications ({applications.length} available)
                    </label>
                    {applications.length > 0 && (
                      <button
                        onClick={selectAllApps}
                        className="text-xs text-blue-400 hover:text-blue-300"
                      >
                        {selectedAppIds.length === applications.length ? 'Deselect All' : 'Select All'}
                      </button>
                    )}
                  </div>
                  
                  {!selectedClientId ? (
                    <p className="text-slate-500 text-sm py-4 text-center">Select a client first</p>
                  ) : applications.length === 0 ? (
                    <p className="text-slate-500 text-sm py-4 text-center">No applications available for this client</p>
                  ) : (
                    <div className="border border-slate-700 rounded-lg max-h-64 overflow-y-auto">
                      {applications.map((app) => (
                        <div
                          key={app.id}
                          onClick={() => toggleAppSelection(app.id)}
                          className={`flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-slate-700/50 border-b border-slate-700/50 last:border-b-0 ${
                            selectedAppIds.includes(app.id) ? 'bg-blue-500/10' : ''
                          }`}
                        >
                          {selectedAppIds.includes(app.id) ? (
                            <CheckSquare className="w-5 h-5 text-blue-400 flex-shrink-0" />
                          ) : (
                            <Square className="w-5 h-5 text-slate-500 flex-shrink-0" />
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-slate-200 truncate">{app.referenceNumber}</p>
                            <p className="text-xs text-slate-500">{app.territoryName}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {selectedAppIds.length > 0 && (
                    <p className="text-sm text-blue-400 mt-2">
                      {selectedAppIds.length} application(s) selected
                    </p>
                  )}
                </div>
                
                <div className="flex justify-end gap-3 pt-4 border-t border-slate-700/50">
                  <button
                    onClick={() => {
                      setShowGenerateModal(false)
                      setSelectedAppIds([])
                    }}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleGenerate}
                    disabled={selectedAppIds.length === 0 || !selectedClientId}
                    className="btn-primary flex items-center gap-2"
                  >
                    <Sparkles className="w-4 h-4" />
                    Generate {selectedAppIds.length > 0 ? `${selectedAppIds.length} Letter(s)` : 'Letters'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Letter Editor Modal */}
      {editingLetter && (
        <LetterEditor
          letter={editingLetter}
          onUpdate={() => {
            fetchLetters()
            fetchStats()
            // Refresh selected letter
            if (selectedLetter?.id === editingLetter.id) {
              api.get(`/api/v1/letters/${editingLetter.id}`).then(res => {
                setSelectedLetter(res.data)
              })
            }
          }}
          onClose={() => setEditingLetter(null)}
        />
      )}
    </div>
  )
}
